/** Prepare freshly fetched ROM bytes for a one-shot authenticated launch. */
import { SYSTEMS } from '../cores/systems.js';
import { extractRomFromZipBoundedAsync } from '../cores/rom-zip.js';
import { sha256Hex } from '../../shared/base-state.js';
import { resolveC64Identity, yieldTask } from './c64-identity.js';
import { CATALOG_HASH_HEADER } from '../../shared/c64-bundle-identity.js';

const unavailable = () => Object.assign(new Error('ROM unavailable'), { code: 'intent_unavailable' });
const current = (signal, isCurrent, deadlineAt) => !signal?.aborted && isCurrent()
  && (deadlineAt == null || performance.now() < deadlineAt);
const MAX_ROM_BYTES = 128 * 1024 * 1024;
const MAX_BUNDLE_BYTES = 16 * 1024 * 1024;
const MAX_RETAINED_ROM_BYTES = 384 * 1024 * 1024;
const MAX_C64_DISK_CHARS = 2 * 1024 * 1024;
const MAX_C64_DISKS = 64;
const MAX_C64_NAME_CHARS = 512;
const YIELD_BYTES = 1024 * 1024;
const yieldToDeadline = () => new Promise(resolve => setTimeout(resolve, 0));

async function hashRomBounded(data, stillCurrent) {
  let hash = 0x811c9dc5;
  const step = 64 * 1024;
  for (let offset = 0; offset < data.length; offset += step) {
    if (!stillCurrent()) return null;
    const end = Math.min(data.length, offset + step);
    for (let i = offset; i < end; i++) {
      hash ^= data[i];
      hash = (hash * 0x01000193) >>> 0;
    }
    if (offset && offset % YIELD_BYTES === 0) await yieldToDeadline();
  }
  return stillCurrent() ? hash.toString(16).padStart(8, '0') : null;
}

function checkC64BundleShape(bundle) {
  if (!bundle || typeof bundle.name !== 'string' || bundle.name.length > MAX_C64_NAME_CHARS
      || !Array.isArray(bundle.disks) || !bundle.disks.length || bundle.disks.length > MAX_C64_DISKS
      || Object.keys(bundle).sort().join(',') !== 'disks,name') throw unavailable();
  let retained = bundle.name.length + 64;
  for (const disk of bundle.disks) {
    if (!disk || typeof disk.name !== 'string' || typeof disk.data !== 'string'
        || Object.keys(disk).sort().join(',') !== 'data,name'
        || disk.name.length > MAX_C64_NAME_CHARS
        || disk.name.length + disk.data.length > MAX_C64_DISK_CHARS) throw unavailable();
    retained += disk.name.length + disk.data.length + 64;
    if (retained > MAX_BUNDLE_BYTES) throw unavailable();
  }
}

const C64_PARSE_WORKER = `
self.onmessage = ({ data }) => {
  try {
    const bundle = JSON.parse(new TextDecoder().decode(data));
    if (!bundle || typeof bundle.name !== 'string' || bundle.name.length > ${MAX_C64_NAME_CHARS}
        || !Array.isArray(bundle.disks) || !bundle.disks.length
        || bundle.disks.length > ${MAX_C64_DISKS}
        || Object.keys(bundle).sort().join(',') !== 'disks,name') throw new Error('invalid bundle');
    let retained = bundle.name.length + 64;
    for (const disk of bundle.disks) {
      if (!disk || typeof disk.name !== 'string' || typeof disk.data !== 'string'
          || Object.keys(disk).sort().join(',') !== 'data,name'
          || disk.name.length > ${MAX_C64_NAME_CHARS}
          || disk.name.length + disk.data.length > ${MAX_C64_DISK_CHARS}) throw new Error('invalid disk');
      retained += disk.name.length + disk.data.length + 64;
      if (retained > ${MAX_BUNDLE_BYTES}) throw new Error('bundle too large');
    }
    self.postMessage({ bundle });
  } catch { self.postMessage({ error: true }); }
};`;

const C64_TRANSFORM_WORKER = `
self.onmessage = async ({ data }) => {
  try {
    let value;
    if (data.task === 'digest') {
      const serialized = JSON.stringify(data.bundle.disks.map(disk => [disk.name, disk.data]));
      if (serialized.length > ${MAX_BUNDLE_BYTES}) throw new Error('bundle too large');
      const bytes = new TextEncoder().encode(serialized);
      if (bytes.byteLength > ${MAX_BUNDLE_BYTES}) throw new Error('bundle too large');
      const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
      value = Array.from(digest, byte => byte.toString(16).padStart(2, '0')).join('');
    } else if (data.task === 'encode') {
      const json = JSON.stringify(data.bundle);
      if (json.length > ${MAX_BUNDLE_BYTES}) throw new Error('bundle too large');
      const parts = [];
      for (let i = 0; i < json.length; i += 16_383) parts.push(btoa(json.slice(i, i + 16_383)));
      value = parts.join('');
    } else throw new Error('unknown task');
    self.postMessage({ value });
  } catch { self.postMessage({ error: true }); }
};`;

function runC64Transform(task, bundle, signal, deadlineAt) {
  return new Promise((resolve, reject) => {
    let worker;
    let url;
    let timer;
    let done = false;
    const finish = (error, value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      try { worker?.terminate(); } catch { /* preserve the result */ }
      if (url) URL.revokeObjectURL(url);
      if (error) reject(unavailable());
      else resolve(value);
    };
    const cancel = () => finish(true);
    try {
      url = URL.createObjectURL(new Blob([C64_TRANSFORM_WORKER], { type: 'text/javascript' }));
      worker = new Worker(url);
    } catch { finish(true); return; }
    worker.onmessage = event => finish(event.data?.error || typeof event.data?.value !== 'string', event.data?.value);
    worker.onerror = cancel;
    signal?.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(cancel, Math.max(0, (deadlineAt ?? performance.now() + 20_000) - performance.now()));
    if (signal?.aborted) return cancel();
    try { worker.postMessage({ task, bundle }); } catch { finish(true); }
  });
}

async function parseC64Bundle(bytes, signal, deadlineAt) {
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  if (typeof Worker !== 'function') {
    // Node test runners have no browser Worker. A browser without one cannot
    // interrupt JSON parsing, so fail closed there.
    if (typeof process === 'undefined' || !process.versions?.node) throw unavailable();
    try {
      const bundle = JSON.parse(new TextDecoder().decode(bytes));
      checkC64BundleShape(bundle);
      if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
      return bundle;
    } catch { throw unavailable(); }
  }
  return new Promise((resolve, reject) => {
    let worker;
    let url;
    let timer;
    let done = false;
    const finish = (error, bundle) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      try { worker?.terminate(); } catch { /* preserve the original failure */ }
      if (url) URL.revokeObjectURL(url);
      if (error) reject(unavailable());
      else resolve(bundle);
    };
    const cancel = () => finish(true);
    try {
      url = URL.createObjectURL(new Blob([C64_PARSE_WORKER], { type: 'text/javascript' }));
      worker = new Worker(url);
    } catch { finish(true); return; }
    worker.onmessage = event => finish(event.data?.error || !event.data?.bundle, event.data?.bundle);
    worker.onerror = cancel;
    signal?.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(cancel, Math.max(0, (deadlineAt ?? performance.now() + 20_000) - performance.now()));
    if (signal?.aborted) return cancel();
    try { worker.postMessage(bytes, [bytes.buffer]); } catch { finish(true); }
  });
}

async function boundedBody(response, cap, signal, deadlineAt) {
  const declared = Number(response.headers?.get?.('content-length'));
  if (Number.isFinite(declared) && declared > cap) throw unavailable();
  // arrayBuffer() would allocate the whole response before any size check.
  if (!response.body?.getReader) throw unavailable();
  const reader = response.body.getReader();
  let bytes = new Uint8Array(Number.isFinite(declared) && declared > 0
    ? Math.min(declared, cap) : Math.min(1024 * 1024, cap));
  let size = 0;
  try {
    while (true) {
      if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
      const { done, value } = await reader.read();
      if (done) break;
      if (value.byteLength > cap - size) throw unavailable();
      const needed = size + value.byteLength;
      if (needed > bytes.byteLength) {
        const grown = new Uint8Array(Math.min(cap, Math.max(needed, bytes.byteLength * 2)));
        grown.set(bytes.subarray(0, size));
        bytes = grown;
      }
      bytes.set(value, size);
      size = needed;
    }
  } catch (error) {
    void reader.cancel().catch(() => {});
    throw error?.code === 'intent_unavailable' ? error : unavailable();
  } finally { reader.releaseLock(); }
  return bytes.subarray(0, size);
}

export async function digestC64Bundle(bundle, { deadlineAt, signal } = {}) {
  checkC64BundleShape(bundle);
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  if (typeof Worker === 'function') return runC64Transform('digest', bundle, signal, deadlineAt);
  if (typeof process === 'undefined' || !process.versions?.node) throw unavailable();
  let size = bundle.name.length + 64;
  let lastYieldSize = 0;
  const parts = [];
  for (const disk of bundle.disks) {
    if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
    size += disk.name.length + disk.data.length + 64;
    parts.push(JSON.stringify([disk.name, disk.data]));
    if (size - lastYieldSize >= YIELD_BYTES || parts.length % 16 === 0) {
      await yieldToDeadline();
      lastYieldSize = size;
    }
  }
  // Disk order, names and the complete disk data are part of the launch
  // identity. This digest is separate from the legacy 8-character game ID.
  const serialized = `[${parts.join(',')}]`;
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  if (serialized.length > MAX_BUNDLE_BYTES) throw unavailable();
  const bytes = new TextEncoder().encode(serialized);
  if (bytes.byteLength > MAX_BUNDLE_BYTES) throw unavailable();
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  const digest = await sha256Hex(bytes);
  if (deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  return digest;
}

export async function encodeC64BundleForLaunch(bundle, { deadlineAt, signal } = {}) {
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  checkC64BundleShape(bundle);
  if (typeof Worker === 'function') return runC64Transform('encode', bundle, signal, deadlineAt);
  if (typeof process === 'undefined' || !process.versions?.node) throw unavailable();
  await yieldToDeadline();
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  let json;
  try { json = JSON.stringify(bundle); } catch { throw unavailable(); }
  if (typeof json !== 'string' || json.length > MAX_BUNDLE_BYTES) throw unavailable();
  const parts = [];
  // A multiple of three preserves the exact btoa(JSON.stringify(bundle)) bytes.
  const step = 16_383;
  for (let i = 0; i < json.length; i += step) {
    if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
    try { parts.push(btoa(json.slice(i, i + step))); } catch { throw unavailable(); }
    if (i && i % (step * 64) === 0) await yieldToDeadline();
  }
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) throw unavailable();
  return parts.join('');
}

export async function revalidatePlayRom(selection, {
  signal, fetchImpl = globalThis.fetch, isCurrent = () => true, deadlineAt,
  catalogLookup = () => null, waitForCatalog = null,
} = {}) {
  const system = SYSTEMS[selection.system];
  const stillCurrent = () => current(signal, isCurrent, deadlineAt);
  if (!system || !stillCurrent()) return false;
  const base = `/api/roms/${selection.system}/${encodeURIComponent(selection.filename)}`;
  const options = { credentials: 'include', cache: 'no-store', signal };
  const wantsBundle = selection.content_digest != null
    || selection.system === 'c64' && !/\.(?:d64|d71|d81|g64|zip|prg|crt|t64|tap|p00)$/i.test(selection.filename);
  if (wantsBundle && !selection.content_digest) {
    throw Object.assign(new Error('C64 bundle digest missing'), { code: 'invalid_selection' });
  }
  const requestedBundle = wantsBundle;
  let res;
  try { res = await fetchImpl(requestedBundle ? `${base}/disks` : base, options); }
  catch { if (!stillCurrent()) return false; throw unavailable(); }
  if (!stillCurrent()) return false;
  if (!res.ok) throw unavailable();
  if (requestedBundle) {
    const bundle = await parseC64Bundle(await boundedBody(res, MAX_BUNDLE_BYTES, signal, deadlineAt), signal, deadlineAt);
    if (!stillCurrent()) return false;
    const digest = await digestC64Bundle(bundle, { deadlineAt, signal });
    if (!stillCurrent()) return false;
    // The verified catalog identity (spec 4.3), from the /disks header or,
    // for a response from an older server, the ROM list.
    const STALE = Symbol('stale');
    let identity;
    try {
      identity = await resolveC64Identity({
        disks: bundle.disks, name: bundle.name, headerHash: res.headers?.get?.(CATALOG_HASH_HEADER) ?? null, signal,
        catalogLookup, waitForCatalog,
        checkpoint: async () => { await yieldTask(signal); if (!stillCurrent()) throw STALE; }, // stillCurrent includes the deadline
      });
    } catch (error) {
      if (error === STALE || !stillCurrent()) return false;
      throw unavailable();
    }
    if (identity.hash !== selection.hash || digest !== selection.content_digest) {
      throw Object.assign(new Error('ROM changed'), { code: 'rom_changed' });
    }
    return { ...selection, kind: 'bundle', romData: bundle, romName: bundle.name,
      content_digest: selection.content_digest, identity };
  }
  let data = await boundedBody(res, MAX_ROM_BYTES, signal, deadlineAt);
  if (!stillCurrent()) return false;
  const downloadedBytes = data.buffer.byteLength;
  const isZip = selection.filename.toLowerCase().endsWith('.zip');
  let romName = selection.filename;
  if (isZip) {
    let extracted;
    try {
      const inflateCap = Math.min(MAX_ROM_BYTES,
        Math.floor((MAX_RETAINED_ROM_BYTES - downloadedBytes) / 6));
      extracted = await extractRomFromZipBoundedAsync(data, system, {
        maxEntryBytes: inflateCap, maxAggregateBytes: inflateCap,
        signal, deadlineAt,
      });
    } catch { throw unavailable(); }
    if (!extracted) throw unavailable();
    data = extracted.data;
    romName = extracted.name;
  }
  if (!stillCurrent()) return false;
  if ((isZip ? downloadedBytes : 0) + data.buffer.byteLength * 5 > MAX_RETAINED_ROM_BYTES) throw unavailable();
  if (!stillCurrent()) return false;
  const hash = await hashRomBounded(data, stillCurrent);
  if (hash === null) return false;
  if (hash !== selection.hash) {
    throw Object.assign(new Error('ROM changed'), { code: 'rom_changed' });
  }
  let romData = '';
  for (let i = 0; i < data.length; i += 8192) {
    if (!stillCurrent()) return false;
    romData += String.fromCharCode(...data.subarray(i, i + 8192));
    if (i && i % YIELD_BYTES === 0) await yieldToDeadline();
  }
  if (!stillCurrent()) return false;
  return stillCurrent()
    ? { ...selection, kind: 'rom', romData, romName } : false;
}
